import { Component, EventEmitter, Inject, Input, OnDestroy, Output } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { RenderEvent } from 'render/render-events/render-events.model';
import { RenderEventsService } from 'render/render-events/render-events.service';
import * as ModalPanelActions from 'render/render-shared/ui/render-modal-panel/store/render-modal-panel.actions';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { RenderState } from 'render/services/render-state/render-state.models';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import { InditexWindow } from 'shared/models/inditex-window.models';

import { Benefit, UserRegistrationStatus } from 'extensions/st/piece-events/front/services/api-events.models';
import { ApiEventsService } from 'extensions/st/piece-events/front/services/api-events.service';
import { SubscribeNewsletterService } from 'extensions/st/slice-drag/front/services/subscribe-newsletter.service';


type ReservationForm = FormGroup<{
  name: FormControl<string>;
  surname: FormControl<string>;
}>;


@Component({
  selector: 'piece-events-reservation-modal',
  templateUrl: './event-reservation-modal.component.html',
  styleUrls: ['./event-reservation-modal.component.scss']
})
export class EventReservationModalComponent implements OnDestroy {

  @Input() public event?: Benefit;
  @Input() public piece?: IdtComponent;
  @Input() public country?: string;
  @Input() public lang?: string;
  @Input() public languageTag?: string;
  @Input() public storeId: string = '';
  @Input() public accessToken?: string;
  @Output() public reservationRegistered = new EventEmitter<UserRegistrationStatus | undefined>();

  public acceptedLegalTerms: boolean = false;
  public acceptedMarketingTerms: boolean = false;
  public reservationForm: ReservationForm;
  private languageDisplayNames?: { of: (code: string) => string | undefined };
  private languageDisplayNamesLocale?: string;
  private readonly destroy$ = new Subject<void>();

  public get isMobile(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public get mail(): string {
    return this.window.inditex?.iUserJSON?.email || '';
  }

  public get phone(): string {
    return this.window.inditex?.iUserJSON?.phones?.[0]?.subscriberNumber || '';
  }

  public get prefix(): string {
    return this.window.inditex?.iUserJSON?.phones?.[0]?.prefixCode || '';
  }

  public get eventLanguageDisplayName(): string {
    const languageCode = this.event?.detailedInfo?.language;
    if (!languageCode) {
      return '';
    }

    const normalizedLanguage = languageCode.toLowerCase().replace('_', '-').split('-')[0];
    const locale = this.lang || 'es';

    if (this.languageDisplayNamesLocale !== locale) {
      this.languageDisplayNames = this.createDisplayNames(locale);
      this.languageDisplayNamesLocale = locale;
    }

    const displayName = this.languageDisplayNames?.of(normalizedLanguage) || normalizedLanguage;
    return displayName.charAt(0).toLocaleUpperCase(locale) + displayName.slice(1);
  }

  public get reservationModalName(): string {
    return `event-reservation-modal-${this.piece?.id}`;
  }

  constructor(
    @Inject('Window') private readonly window: InditexWindow,
    private readonly store: Store<RenderState>,
    private readonly apiEventsService: ApiEventsService,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly subscribeNewsletterService: SubscribeNewsletterService,
    private readonly renderEventsService: RenderEventsService
  ) {
    this.reservationForm = this.formBuilder.group({
      name: [this.window.inditex?.iUserJSON?.firstName || '', [Validators.required]],
      surname: [this.window.inditex?.iUserJSON?.lastName || '', [Validators.required]]
    });
  }

  public onRequiredConsentChange(): void {
    this.acceptedLegalTerms = !this.acceptedLegalTerms;
  }

  public isComingSoon(event?: Benefit): boolean {
    const detailedInfo = event?.detailedInfo;
    if (!detailedInfo?.isComingSoon) {
      return false;
    }

    const comingSoonEndDate = detailedInfo.comingSoonEndDateTime
      ? new Date(detailedInfo.comingSoonEndDateTime)
      : undefined;

    if (!comingSoonEndDate || Number.isNaN(comingSoonEndDate.getTime())) {
      return false;
    }

    return new Date() < comingSoonEndDate;
  }

  public onClose(): void {
    this.store.dispatch(ModalPanelActions.closeModal({
      modalName: this.reservationModalName
    }));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(): void {
    this.reservationForm.markAllAsTouched();
    if (this.reservationForm.invalid || !this.acceptedLegalTerms) {
      return;
    }

    const benefitId = this.event?.id;
    const firstName = this.reservationForm.controls.name.value.trim();
    const lastName = this.reservationForm.controls.surname.value.trim();
    const phonePrefix = this.window.inditex?.iUserJSON?.phones?.[0]?.prefixCode || '';
    const phoneNumber = this.window.inditex?.iUserJSON?.phones?.[0]?.subscriberNumber || '';
    const email = this.mail;

    if (!benefitId || !this.languageTag || !this.storeId || !this.country || !this.lang) {
      return;
    }

    this.subscribeNewsletterService.search(
      this.country,
      this.lang,
      this.accessToken
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe((response) => {
      if (response && Object.prototype.hasOwnProperty.call(response, 'newsletter') && !response.newsletter) {
        this.emitSubscribeNewsletterEvent();
      }
    });

    this.apiEventsService.registerReservation(
      this.languageTag,
      this.storeId,
      benefitId,
      this.accessToken,
      {
        customerInfo: {
          firstName,
          lastName,
          phone: `${phonePrefix}${phoneNumber}`,
          email
        }
      }
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe((response) => {
      this.reservationRegistered.emit(response?.userEventBooking?.status);
      this.onClose();
    });
  }

  private createDisplayNames(locale: string): { of: (code: string) => string | undefined } | undefined {
    const DisplayNamesConstructor = (Intl as { DisplayNames?: new (locales: string[], options: { type: 'language' }) => { of: (code: string) => string | undefined } }).DisplayNames;
    if (!DisplayNamesConstructor) {
      return undefined;
    }

    try {
      return new DisplayNamesConstructor([locale], { type: 'language' });
    } catch {
      return undefined;
    }
  }

  private emitSubscribeNewsletterEvent(): void {
    const event = new Event('subscribeNewsletter');
    const email = this.mail;
    const birthdate = this.window.inditex?.iUserJSON?.birthDate;
    const renderEvent: RenderEvent = {
      event,
      extra: { type: 'subscribeNewsletter' },
      location: null,
      data: {
        email,
        birthdate
      }
    };
    this.renderEventsService.events.next(renderEvent);
  }

}
